import { useContext, useMemo } from 'react';

import type { CurrencyData } from '@/api/config/getCurrencies.ts';
import { useCurrenciesQuery } from '@/api/config/index.ts';
import { SgCurrencyPairPicker } from '@/components/sg-design-system/components/input/SgCurrencyPairPicker/index.tsx';
import { userContext } from '@/context/UserContext.tsx';
import type { OrderType } from '@/store/orders/state.ts';

type TradeType = 'Cash' | 'Swap' | 'Order' | 'Option' | 'Accumulator' | 'Bulk' | 'FlexiForward';

export type CurrencyPickerProps = {
  value?: string;
  onChange: (value: string) => void;
  'data-e2e'?: string;
  tradeType?: TradeType;
  orderType?: OrderType;
};

const CurrencyPairPicker = (props: CurrencyPickerProps) => {
  const { value = '', onChange, 'data-e2e': dataE2e = 'currency-pair-input', orderType, tradeType } = props;
  const user = useContext(userContext);
  const nonDeliverable = user?.currentUser.productsAccess.nonDeliverable;

  const {
    isError: isCurrencyPairsError,
    isLoading: isCurrencyPairsLoading,
    data: { dict } = {},
  } = useCurrenciesQuery();

  const availableCurrencyPairsFiltered = useMemo(
    () => filterCurrencyPairs(dict, nonDeliverable ?? false, tradeType, orderType),
    [dict, nonDeliverable, tradeType, orderType],
  );

  if (isCurrencyPairsLoading) {
    return 'Loading...';
  }

  if (isCurrencyPairsError || availableCurrencyPairsFiltered.length === 0) {
    return '/!\\ no data ';
  }

  return (
    <SgCurrencyPairPicker
      value={value}
      onChange={(newValue) => onChange(newValue ?? '')}
      data-e2e={dataE2e}
      currencyPairs={availableCurrencyPairsFiltered}
    />
  );
};

export default CurrencyPairPicker;

function filterCurrencyPairs(
  currencyPairs: Record<string, CurrencyData> | undefined,
  nonDeliverable: boolean,
  tradeType?: TradeType,
  _orderType?: OrderType,
): string[] {
  const filteredCurrencies: () => CurrencyData[] = () => {
    switch (tradeType) {
      case 'Cash':
      case 'Swap':
        return Object.entries(currencyPairs ?? {})
          .filter(([_, data]) => data.canBeDelivered || nonDeliverable)
          .map(([_, data]) => data)
          .sort(sortCurrencies);
      case 'Order':
        return Object.entries(currencyPairs ?? {})
          .filter(([_, data]) => data.canBeDelivered || nonDeliverable)
          .map(([_, data]) => data)
          .sort(sortCurrencies);
      case 'Option':
      case 'Accumulator':
        return Object.entries(currencyPairs ?? {})
          .filter(([_, data]) => data.isOptionCurrency)
          .map(([_, data]) => data)
          .sort(sortCurrencies);
      case 'Bulk':
        return Object.entries(currencyPairs ?? {})
          .filter(([_, data]) => data.canBeDelivered)
          .map(([_, data]) => data)
          .sort(sortCurrencies);
      case 'FlexiForward':
        return Object.entries(currencyPairs ?? {})
          .filter(([_, data]) => data.canBeDelivered && data.isOptionCurrency)
          .map(([_, data]) => data)
          .sort(sortCurrencies);
      default:
        return Object.values(currencyPairs ?? {});
    }
  };
  return filteredCurrencies().map((currencyPair) => `${currencyPair.ccy1}/${currencyPair.ccy2}`);
}

export function sortCurrencies(a: CurrencyData, b: CurrencyData) {
  if (a.isInverted && !b.isInverted) {
    return 1;
  }
  if (!a.isInverted && b.isInverted) {
    return -1;
  }
  if (a.pair < b.pair) {
    return -1;
  }

  if (a.pair > b.pair) {
    return 1;
  }

  return 0;
}
